import React, { useState } from "react";
import ReactDOM from "react-dom";
import { Share2, X, Check, Copy, Link2 } from "lucide-react";

const ShareInvitation = ({ roomId, scheduledAt, onClose }) => {
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copied, setCopied] = useState(false);

  const baseUrl = window.location.origin;
  const interviewLink = `${baseUrl}/interview/${roomId}`;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const shareMessage = `You're invited to join an interview session scheduled for ${formatDate(scheduledAt)}. Click the link to join: ${interviewLink}`;

  const shareOnWhatsApp = () => {
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareMessage)}`;
    window.open(whatsappUrl, "_blank");
  };

  const shareOnTelegram = () => {
    const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(interviewLink)}&text=${encodeURIComponent(`Interview scheduled for ${formatDate(scheduledAt)}`)}`;
    window.open(telegramUrl, "_blank");
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(interviewLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {}
  };

  return (
    <>
      <button
        onClick={() => setShowShareMenu(!showShareMenu)}
        className="btn btn-md border border-amber-400/25 bg-amber-400/10 text-amber-200 hover:bg-amber-400/20"
        title="Share invitation"
      >
        <Share2 className="h-4 w-4" />
        Share
      </button>

      {showShareMenu &&
        ReactDOM.createPortal(
          <div
            className="fixed inset-0 flex items-center justify-center"
            style={{ zIndex: 9999, backgroundColor: "rgba(42, 20, 21, 0.7)" }}
          >
            <div
              className="absolute inset-0 backdrop-blur-sm"
              onClick={() => setShowShareMenu(false)}
            ></div>

            <div className="relative w-full max-w-md animate-scale-in overflow-hidden rounded-2xl border border-white/10 bg-cocoa-700 shadow-modal">
              <div className="flex items-center justify-between border-b border-white/10 px-6 py-4">
                <div>
                  <p className="text-base font-semibold text-cream-100">
                    Share Interview Invitation
                  </p>
                  <p className="mt-0.5 text-xs text-amber-100/50">
                    {formatDate(scheduledAt)}
                  </p>
                </div>
                <button
                  onClick={() => setShowShareMenu(false)}
                  className="flex h-8 w-8 items-center justify-center rounded-lg text-amber-200/70 transition-colors hover:bg-white/10 hover:text-amber-100"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="p-3">
                <button
                  onClick={() => {
                    shareOnWhatsApp();
                    setShowShareMenu(false);
                  }}
                  className="flex w-full items-center gap-4 rounded-xl px-3 py-3 text-left transition-colors hover:bg-white/5"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-500">
                    <svg
                      className="h-5 w-5 text-white"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-cream-100">WhatsApp</p>
                    <p className="text-xs text-amber-100/50">Share via WhatsApp</p>
                  </div>
                </button>

                <button
                  onClick={() => {
                    shareOnTelegram();
                    setShowShareMenu(false);
                  }}
                  className="flex w-full items-center gap-4 rounded-xl px-3 py-3 text-left transition-colors hover:bg-white/5"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-sky-500">
                    <svg
                      className="h-5 w-5 text-white"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-cream-100">Telegram</p>
                    <p className="text-xs text-amber-100/50">Share via Telegram</p>
                  </div>
                </button>

                <button
                  onClick={copyLink}
                  className="flex w-full items-center gap-4 rounded-xl px-3 py-3 text-left transition-colors hover:bg-white/5"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-400/15 ring-1 ring-inset ring-amber-400/30">
                    {copied ? (
                      <Check className="h-5 w-5 text-emerald-300" />
                    ) : (
                      <Copy className="h-5 w-5 text-amber-300" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-cream-100">
                      {copied ? "Copied!" : "Copy Link"}
                    </p>
                    <p className="flex items-center gap-1 truncate text-xs text-amber-100/50">
                      <Link2 className="h-3 w-3 shrink-0" />
                      <span className="truncate font-mono">{interviewLink}</span>
                    </p>
                  </div>
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default ShareInvitation;
